import { Alert, AlertIcon } from "@chakra-ui/react";
import { useToast } from "@chakra-ui/toast";
import { FC, useCallback } from "react";
import { graphql, useFragment } from "react-relay";
import { useParams } from "react-router-dom";

import { UrlMappingListTabContainer_deleteMutation } from "~/src/__relay_artifacts__/UrlMappingListTabContainer_deleteMutation.graphql";
import { UrlMappingListTabContainer_user$key } from "~/src/__relay_artifacts__/UrlMappingListTabContainer_user.graphql";
import { useMutationCommit } from "~/src/lib/react-relay";

import { UrlMappingListTab, UrlMappingListTabProps } from "./presentations";
import { useUpdateUrlMappingActive } from "./useUpdateUrlMappingActive";

export type Props = {
  userRef: UrlMappingListTabContainer_user$key;
};

const userFragment = graphql`
  fragment UrlMappingListTabContainer_user on User {
    enableBackendUrlMapping
    ...UrlMappingListTab_user
  }
`;

const deleteMutation = graphql`
  mutation UrlMappingListTabContainer_deleteMutation(
    $input: DeleteUrlMappingInput!
    $connections: [ID!]!
  ) {
    deleteUrlMapping(input: $input) {
      deletedUrlMappingId @deleteEdge(connections: $connections)
    }
  }
`;

export const UrlMappingListTabContainer: FC<Props> = ({ userRef }) => {
  const user = useFragment(userFragment, userRef);
  const { siteSlug = "" } = useParams();
  const toast = useToast();
  const { updateUrlMappingActive } = useUpdateUrlMappingActive();
  const deleteMutate =
    useMutationCommit<UrlMappingListTabContainer_deleteMutation>(
      deleteMutation
    );

  const handleDeleteClick = useCallback(
    async (urlMappingId: string, connectionId: string) => {
      try {
        const res = await deleteMutate({
          variables: {
            input: { urlMappingId },
            connections: [connectionId],
          },
        });
        const deletedUrlMappingId = res.deleteUrlMapping?.deletedUrlMappingId;
        if (!deletedUrlMappingId) throw new Error("assertion failed");

        toast({ title: "URLマッピングを削除しました", status: "success" });
      } catch {
        toast({ title: "URLマッピングの削除に失敗しました", status: "error" });
      }
    },
    [deleteMutate, toast]
  );

  const handleActiveChange = useCallback<
    UrlMappingListTabProps["onActiveChange"]
  >(
    async (urlMapping) => {
      try {
        const { updateUrlMapping } = await updateUrlMappingActive({
          variables: {
            input: {
              urlMappingId: urlMapping.id,
              active: !urlMapping.active,
            },
          },
        });
        if (!updateUrlMapping?.urlMapping) throw new Error("assertion failed");
        toast({ title: "ステータスを更新しました", status: "success" });
      } catch {
        toast({ title: "ステータスの更新に失敗しました", status: "error" });
      }
    },
    [toast, updateUrlMappingActive]
  );

  return (
    <>
      {!user.enableBackendUrlMapping && (
        <Alert status="warning" mb="16px">
          <AlertIcon />
          バックエンドURLマッピングは現在オフです。ルールは保存されますが、SDKへの反映にはフラグの有効化が必要です。
        </Alert>
      )}
      <UrlMappingListTab
        userRef={user}
        siteSlug={siteSlug}
        onDeleteClick={handleDeleteClick}
        onActiveChange={handleActiveChange}
      />
    </>
  );
};
